import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { formatDate, useModalControl, usePopoverMenu } from '@frontend/design-system';
import { useScheduleSettingsSmallMode } from '../../../../../../hooks/settings/use-schedule-settings-small-mode';
import { openSchedulePulseRequest } from '../../../../../../stores/schedule-pulse.store';
import type { ScheduleListItemVariant } from '../ScheduleRequestList/components/schedule-request-item-header-ui';
import type { ScheduleRequestActionCalledFrom } from './use-schedule-request-actions';
import { useScheduleRequestNotificationsAsRead } from './use-schedule-request-notifications-as-read';

export type RequestSource = 'AI' | 'partner' | 'online_scheduling';

export const useScheduleRequestItemHeaderController = ({
  requestVersion,
  locationId,
  scheduleRequestId,
  appointmentTime,
  variant,
  requestSource,
  deleteRequestModalProps,
  dismissRequestModalProps,
  rejectRequestModalProps,
  onCompleteRequest,
  onDismissRequestIntent,
  onMessage,
  onPhoneCall,
}: {
  requestVersion: string;
  scheduleRequestId: string;
  locationId: string;
  appointmentTime: string;
  variant?: ScheduleListItemVariant;
  requestSource: RequestSource;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  dismissRequestModalProps?: ReturnType<typeof useModalControl>;
  rejectRequestModalProps?: ReturnType<typeof useModalControl>;
  onCompleteRequest?: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onDismissRequestIntent?: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall?: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
}) => {
  const { t } = useTranslation('schedule-request');
  const user = getUser();
  const userId = user?.userID ?? '';
  const { markNotificationsAsRead } = useScheduleRequestNotificationsAsRead();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const { getLocationName } = useAppScopeStore();
  const locationName = getLocationName(locationId);
  const shouldUseV3 = requestVersion !== 'v2' && isScheduleV3FlagEnabled;
  const appointmentTimeLabel =
    shouldUseV3 && variant !== 'widget' && !isSmallMode
      ? t('Requested on {{requestTime}}', { requestTime: formatDate(appointmentTime, 'MMM D YYYY, h:mm A') })
      : formatDate(appointmentTime, 'MMM D YYYY, h:mm A');

  const {
    getMenuProps,
    getTriggerProps,
    close: closePopOverMenu,
  } = usePopoverMenu({
    placement: 'bottom-start',
  });

  const markAndClose = () => {
    markNotificationsAsRead(scheduleRequestId);
    closePopOverMenu();
  };

  const trackRequestAction = (actionType: 'view_request_details' | 'review_request') => {
    SchedulerV3.trackPendoEvent('request_action_click', {
      appointmentRequestId: scheduleRequestId,
      userId,
      locationId,
      actionCalledFrom: 'action_menu',
      requestSource,
      actionType,
    });
  };

  const handleViewRequestMenuItemClick = () => {
    trackRequestAction('view_request_details');
    openSchedulePulseRequest(locationId, scheduleRequestId);
    markAndClose();
  };

  const handleReviewRequestMenuItemClick = (onReviewRequest: () => void) => {
    trackRequestAction('review_request');
    onReviewRequest();
    markAndClose();
  };

  const handleMessageMenuItemClick = async () => {
    onMessage(requestSource !== 'online_scheduling', 'action_menu');
    markAndClose();
  };

  const handleCallMenuItemClick = async () => {
    onPhoneCall?.('action_menu');
    markAndClose();
  };

  const handleDoneMenuItemClick = () => {
    onCompleteRequest?.('action_menu');
    markAndClose();
  };

  const handleDeleteMenuItemClick = () => {
    deleteRequestModalProps.openModal();
    markAndClose();
  };

  const handleDismissMenuItemClick = () => {
    onDismissRequestIntent?.('action_menu');
    dismissRequestModalProps?.openModal();
    markAndClose();
  };

  const handleRejectRequestMenuItemClick = () => {
    rejectRequestModalProps?.openModal();
    markAndClose();
  };

  return {
    shouldUseV3,
    locationName,
    appointmentTimeLabel,
    getMenuProps,
    getTriggerProps,
    handleViewRequestMenuItemClick,
    handleReviewRequestMenuItemClick,
    handleRejectRequestMenuItemClick,
    handleCallMenuItemClick,
    handleMessageMenuItemClick,
    handleDeleteMenuItemClick,
    handleDismissMenuItemClick,
    handleDoneMenuItemClick,
  };
};
